import { useEffect, useState } from "react";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";

// PostHog retorna `undefined` indefinidamente quando a flag ainda não existe no
// projeto — não só enquanto está carregando. Sem tratar isso, uma página que usa
// `undefined` como "carregando" (return null) fica em tela branca pra sempre.
//
// Em vez de adivinhar com um timeout fixo, esperamos o sinal real do client
// (`onFeatureFlags`/`hasLoadedFlags`): assim que o PostHog confirma que já buscou
// as flags do servidor, se a flag ainda estiver `undefined` é seguro tratar como
// desligada (fato consumado, não suposição). O timeout abaixo só existe como rede
// de segurança pro caso do PostHog nunca conseguir carregar de fato (ad-blocker, CSP).
const FLAGS_LOAD_FALLBACK_MS = 8000;

export function usePostHogFeatureFlag(flagKey: string): boolean | undefined {
  const client = usePostHog();
  const enabled = useFeatureFlagEnabled(flagKey);
  const [flagsSettled, setFlagsSettled] = useState(
    () => client?.featureFlags?.hasLoadedFlags ?? false
  );

  useEffect(() => {
    if (!client || flagsSettled) return;
    const unsubscribe = client.onFeatureFlags(() => setFlagsSettled(true));
    const timer = setTimeout(() => setFlagsSettled(true), FLAGS_LOAD_FALLBACK_MS);
    return () => {
      unsubscribe?.();
      clearTimeout(timer);
    };
  }, [client, flagsSettled]);

  if (enabled !== undefined) return enabled;
  return flagsSettled ? false : undefined;
}
